import { useState, useEffect, useCallback } from "react";
import { Header } from "@/components/Header";
import { PromptForm } from "@/components/PromptForm";
import { SVGViewer } from "@/components/SVGViewer";
import { ShowcaseGrid } from "@/components/ShowcaseGrid";
import { Toaster } from "@/components/ui/toaster";
import { toast } from "@/hooks/use-toast";
import { capture, identify } from "@/lib/analytics";
import { useSession } from "@/hooks/use-session";
import type { Animation, GenerateResponse, HistoryEntry, Version } from "@/lib/types";

export default function App() {
  const { session, isLoading: isLoadingSession } = useSession();

  useEffect(() => {
    if (session?.user?.id) {
      identify(session.user.id, {
        email: session.user.email,
        name: session.user.name,
      });
    }
  }, [session]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("subscribed") === "true") {
      toast({
        title: "Welcome aboard!",
        description: "Your subscription has been activated. Enjoy your new features!",
      });
      // Clean up URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  // Current displayed result
  const [result, setResult] = useState<GenerateResponse | null>(null);
  // Full Gemini chat history for the current branch
  const [history, setHistory] = useState<HistoryEntry[]>([]);
  // Every version ever generated in this session (for revert)
  const [versions, setVersions] = useState<Version[]>([]);
  // Which version index is currently shown
  const [versionIndex, setVersionIndex] = useState(0);

  const handleRefineResult = (data: GenerateResponse, refinementPrompt: string) => {
    const newHistory: HistoryEntry[] = [
      ...history,
      { role: "user", content: refinementPrompt },
      { role: "model", content: data.svg },
    ];
    const v: Version = { 
      id: data.id, 
      versionId: data.versionId, 
      prompt: refinementPrompt, 
      svg: data.svg, 
      historySnapshot: newHistory,
      is_public: data.is_public,
      user_id: data.user_id
    };
    // Discard any versions after the current index (like a normal undo stack)
    const newVersions = [...versions.slice(0, versionIndex + 1), v];
    setResult(data);
    setHistory(newHistory);
    setVersions(newVersions);
    setVersionIndex(newVersions.length - 1);
  };

  const handleRevert = (index: number) => {
    const v = versions[index];
    setResult({ 
      id: v.id, 
      versionId: v.versionId, 
      prompt: v.prompt, 
      svg: v.svg,
      is_public: v.is_public,
      user_id: v.user_id
    });
    setHistory(v.historySnapshot);
    setVersionIndex(index);
    // Update the gallery to show this version (fire and forget)
    fetch(`/api/pin/${v.id}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ versionId: v.versionId }),
    }).catch(() => {});
  };

  const handleReset = () => {
    setResult(null);
    setHistory([]);
    setVersions([]);
    setVersionIndex(0);
  };

  useEffect(() => {
    if (result) return;
    const params = new URLSearchParams(window.location.search);
    let id = params.get("id");
    let initialPrompt = "Shared animation";

    // Also check for /v/slug in pathname
    const vMatch = window.location.pathname.match(/^\/v\/([a-z0-9-]+)$/);
    if (!id && vMatch) {
      const slug = vMatch[1];
      const parts = slug.split("-");
      id = parts.pop() || null;
      if (parts.length > 0) {
        initialPrompt = parts.join(" ").replace(/-/g, " ");
        // Capitalize first letter
        initialPrompt = initialPrompt.charAt(0).toUpperCase() + initialPrompt.slice(1);
      }
    }

    if (!id || (!/^[0-9a-f]{32}$/.test(id) && !/^[0-9a-f]{8}$/.test(id))) return;

    let cancelled = false;

    const loadShared = async () => {
      try {
        const res = await fetch(`/api/svg/${id}`);
        if (!res.ok) return;
        const svg = await res.text();
        if (cancelled) return;
        
        const initialHistory: HistoryEntry[] = [
          { role: "user", content: `Create an animated SVG for: ${initialPrompt}` },
          { role: "model", content: svg },
        ];
        const v: Version = { id, versionId: id, prompt: initialPrompt, svg, historySnapshot: initialHistory };
        setResult({ id, versionId: id, prompt: initialPrompt, svg });

        setHistory(initialHistory);
        setVersions([v]);
        setVersionIndex(0);
        window.scrollTo({ top: 0, behavior: "smooth" });
      } catch {
        // ignore
      }
    };

    void loadShared();

    return () => {
      cancelled = true;
    };
  }, [result]);

  // Load a gallery animation into the editor
  const handleEdit = useCallback((animation: Animation, svgContent: string) => {
    capture("gallery_edit_opened", { animation_id: animation.id });
    const initialHistory: HistoryEntry[] = [
      { role: "user", content: `Create an animated SVG for: ${animation.prompt}` },
      { role: "model", content: svgContent },
    ];
    const v: Version = {
      id: animation.id,
      versionId: animation.id,
      prompt: animation.prompt,
      svg: svgContent,
      historySnapshot: initialHistory,
      is_public: animation.is_public,
      user_id: animation.user_id ?? undefined,
    };
    setResult({ 
      id: animation.id, 
      versionId: animation.id, 
      prompt: animation.prompt, 
      svg: svgContent,
      is_public: animation.is_public,
      user_id: animation.user_id ?? undefined
    });
    setHistory(initialHistory);
    setVersions([v]);
    setVersionIndex(0);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const handleManageSubscription = async (e: React.MouseEvent) => {
    e.preventDefault();
    try {
      const res = await fetch("/api/user/portal", { method: "POST" });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || "Failed to open portal");
      }
      const { url } = await res.json();
      window.open(url, "_blank");
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Could not open portal",
        description: err instanceof Error ? err.message : "Please try again later.",
      });
      // Fallback to general settings if we can't get a specific session
      window.open("https://polar.sh/settings", "_blank");
    }
  };

  return (
    <div className="min-h-screen bg-background font-sans selection:bg-primary/30">
      <Header session={session} isLoadingSession={isLoadingSession} />

      <main className="max-w-6xl mx-auto px-6 py-20 space-y-12">
        {result ? (
          <SVGViewer
            result={result}
            history={history}
            versions={versions}
            versionIndex={versionIndex}
            onRefineResult={handleRefineResult}
            onRevert={handleRevert}
            onReset={handleReset}
          />
        ) : (
          <PromptForm session={session} />
        )}
      </main>

      <div className="max-w-6xl mx-auto px-6 py-20">
        <ShowcaseGrid onEdit={handleEdit} session={session} />
      </div>

      <Toaster />

      <footer className="mt-20 py-12 px-6 border-t border-border/10 bg-muted/30">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-8 text-center md:text-left">
          <div className="space-y-2">
            <p className="text-sm font-semibold tracking-tight uppercase text-foreground/80">
              VectorAnimation
            </p>
            <p className="text-xs text-muted-foreground">
              &copy; {new Date().getFullYear()} SQRM ApS. All rights reserved.
            </p>
          </div>
          
          <div className="flex items-center gap-6 flex-wrap justify-center">
            <a href="mailto:help@vectoranimation.com" className="text-xs text-muted-foreground hover:text-foreground transition-colors">
              Support
            </a>
            <a href="/privacy.html" className="text-xs text-muted-foreground hover:text-foreground transition-colors">Privacy</a>
            <a href="/terms.html" className="text-xs text-muted-foreground hover:text-foreground transition-colors">Terms</a>
            <button 
              onClick={handleManageSubscription}
              className="text-xs font-medium text-primary hover:text-primary/80 transition-colors"
            >
              Account
            </button>
          </div>
        </div>
      </footer>
    </div>
  );
}
